/**
 * The review lane: the screen where footage becomes money.
 *
 * The behaviour here is a faithful port of the server-rendered console's client
 * module, and the rules it enforces are the same rules, for the same reasons:
 *
 * **The client never sends a duration or an amount.** It sends marked spans and
 * a decision. The figure on screen is labelled an estimate because it is one —
 * the server computes money, rounds it in exactly one function, and the number
 * a collector is paid comes back from that. A client-side total that happened
 * to agree would be a second rounding site waiting to disagree.
 *
 * **A verdict is idempotent on a uuid this client generates.** The id is minted
 * when the episode is claimed, not when commit is pressed, so a double-tap and
 * a retry after a timeout both carry the same id and the unique index decides.
 * Through `settlements_review_key`, a second review row is a second payment.
 *
 * **The lease is held explicitly and released honestly.** A heartbeat every
 * 60s, a `sendBeacon` release on unload, and a 409 anywhere means somebody else
 * has this episode now — which is a state to explain, never an error to retry.
 *
 * The composition is the theatre: near-black around the video in both themes,
 * because `VQ-DARK` and `VQ-OVEREXPOSED` are reject reasons and bright chrome
 * bordering footage biases a call somebody is paid on. The rails carrying
 * metadata and controls stay on the light shell.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '../components/shell/AppShell.tsx';
import { Button, Key } from '../components/ui/button.tsx';
import { Field, FlagRow, Problem, Skeleton } from '../components/ui/primitives.tsx';
import { guideBlocksKeys } from '../components/guide/useGuide.ts';
import { IconKeyboard, IconPartial, IconPass, IconReject } from '../components/icons.tsx';
import { MESSAGES } from '@playerone/api/i18n';
import { api, ApiError, type Claim, type DemoReviewAction, type ReasonCode, type ReviewQueue, type ReviewCatalogItem, type Verdict } from '../lib/api.ts';
import { ReviewCatalog, RecordingPreview, DemoReviewControls, optimizedUrl } from './ReviewCatalog.tsx';
import { Scrubber, markInSpan, markOutSpan, clearSpanAt, type Span } from './ReviewTimeline.tsx';
import { commitFailure, type CommitFailure } from './refusal.ts';
import { duration, money, signedPercent, signedSeconds } from '../lib/format.ts';
import { cn } from '../lib/cn.ts';
import { uuid } from '../lib/uuid.ts';

const RATES = [0.5, 0.75, 1, 1.5, 2, 3, 4] as const;
const HEARTBEAT_MS = 60_000;

export function ReviewScreen() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<ReviewQueue>('standard');
  const queueRef = useRef<ReviewQueue>('standard');
  const episodeRef = useRef<string | null>(null);
  const claiming = useRef(false);
  const mounted = useRef(false);

  const [episode, setEpisode] = useState<Claim | null>(null);
  const [selected, setSelected] = useState<ReviewCatalogItem | null>(null);
  const queues: ReviewQueue[] = ['standard', 'privacy', 'second_review'];
  const catalogs = useQueries({ queries: queues.map(lane => ({ queryKey: ['review', 'catalog', lane], queryFn: () => api.reviewCatalog(lane), retry: false })) });
  const catalog = catalogs[queues.indexOf(queue)]!;
  const [verdictId, setVerdictId] = useState<string>(() => uuid());
  const [spans, setSpans] = useState<Span[]>([]);
  const [decision, setDecision] = useState<Verdict | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [position, setPosition] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [partIndex, setPartIndex] = useState(0);
  const [originalQuality, setOriginalQuality] = useState(false);
  const [lost, setLost] = useState<'lease' | 'media' | null>(null);
  /**
   * A refusal the server named, and whether it has been parked.
   *
   * Separate from `lost` because it is a different fact with a different
   * action: `lost` means the episode is no longer this reviewer's, a refusal
   * means it is still theirs and the server will not take a verdict on it.
   * Conflating the two is the defect — see `commitFailure`.
   */
  const [refused, setRefused] = useState<CommitFailure & { kind: 'refused' } | null>(null);
  const [leaseError, setLeaseError] = useState<unknown>(null);
  const [held, setHeld] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [claimedAt, setClaimedAt] = useState<number>(() => Date.now());

  const videoRef = useRef<HTMLVideoElement>(null);

  const reasonQuery = useQuery({
    queryKey: ['reasons'],
    queryFn: () => api.reasons(),
    staleTime: Infinity,
  });

  /** Reset everything that belongs to one episode. */
  const adopt = useCallback((claim: Claim | null) => {
    episodeRef.current = claim?.episode_id ?? null;
    setEpisode(claim);
    setVerdictId(uuid());
    setSpans([]);
    setDecision(null);
    setReasons([]);
    setNote('');
    setPosition(0);
    setPartIndex(0);
    setOriginalQuality(false);
    setPlaying(false);
    setLost(null);
    setLeaseError(null);
    setRefused(null);
    setHeld(false);
    setHoldReason('');
    setClaimedAt(Date.now());
  }, []);

  /**
   * Take the next episode — after giving back the one we are holding.
   *
   * The release is not optional and its failure is not swallowed. A lease we
   * still hold on footage that will not play stays held for the full lease
   * window, so claiming another one without releasing this one walks the queue
   * and locks every episode the reviewer touches. If the release itself fails —
   * the network, the server — the claim does not happen either: the mutation
   * ends in its error state, the screen keeps the action that got here, and
   * pressing it again tries the whole thing again. Claiming anyway would turn
   * one stuck lease into two.
   *
   * Releasing something already gone is not an error: the server answers
   * `released: false` and logs nothing, which is what the lease-expired path
   * wants.
   */
  /**
   * What the claim releases before it claims. A ref, not the state, so the
   * mutation is built once instead of on every episode.
   */
  const claim = useMutation({
    mutationFn: async ({ nextQueue, item }: { nextQueue: ReviewQueue; item?: ReviewCatalogItem }) => {
      const held = episodeRef.current;
      if (held !== null) await api.release(held);
      adopt(null);
      setSelected(item ?? null);
      if (!mounted.current) return null;
      return item?.claimable && !item.demo_override ? api.claimNext(nextQueue, item.episode_id) : null;
    },
    onSuccess: (next) => {
      if (mounted.current) adopt(next);
      else if (next) api.releaseOnUnload(next.episode_id);
    },
    onSettled: () => { claiming.current = false; void queryClient.invalidateQueries({ queryKey: ['review', 'catalog'] }); },
  });

  const claimMutate = claim.mutate;
  useEffect(() => {
    if (selected && !claim.isPending) document.querySelector('.review-workbench, .review-preview-only')?.scrollIntoView?.({ block: 'start' });
  }, [selected, episode, claim.isPending]);
  const requestClaim = useCallback((nextQueue = queueRef.current, item?: ReviewCatalogItem) => {
    // Guard synchronously: double clicks and StrictMode effects precede the pending render.
    if (claiming.current || !mounted.current) return;
    claiming.current = true;
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    claimMutate({ nextQueue, item });
  }, [claimMutate]);

  const demo = useMutation({
    mutationFn: async ({ item, action, reason }: { item: ReviewCatalogItem; action: DemoReviewAction; reason: string }) => {
      // A simulated decision never shares the live verdict's lease or mutation.
      if (episodeRef.current) await api.release(episodeRef.current);
      adopt(null);
      const result = await api.demoReview(item.episode_id, action, reason);
      if (!result) throw new Error('Demo action returned no result');
      queueRef.current = result.queue;
      setQueue(result.queue);
      setSelected({ ...item, queue: result.queue, claimable: false, demo_override: { decision: result.decision, original_queue: result.original_queue } });
      await queryClient.invalidateQueries({ queryKey: ['review', 'catalog'] });
      const fresh = await api.reviewCatalog(result.queue);
      if (fresh) {
        queryClient.setQueryData(['review', 'catalog', result.queue], fresh);
        const updated = fresh.items.find(row => row.episode_id === item.episode_id);
        if (updated) setSelected(updated);
      }
    },
    onSettled: () => { claiming.current = false; },
  });

  /** Browsing does not take a lease. Claim only the recording the reviewer selects. */
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (episodeRef.current) api.releaseOnUnload(episodeRef.current);
    };
  }, []);

  /* ---------------------------------------------------------------------
     The lease: a heartbeat while working, a beacon on the way out.
     ------------------------------------------------------------------ */

  const episodeId = episode?.episode_id ?? null;

  useEffect(() => {
    if (episodeId === null || lost !== null) return;
    const timer = setInterval(() => {
      api.heartbeat(episodeId).catch((err) => {
        if (episodeRef.current !== episodeId || !mounted.current) return;
        if (err instanceof ApiError && err.isReassigned) {
          setLost('lease');
          setLeaseError(err);
        }
      });
    }, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [episodeId, lost]);

  useEffect(() => {
    if (episodeId === null) return;
    const release = () => api.releaseOnUnload(episodeId);
    window.addEventListener('pagehide', release);
    return () => {
      window.removeEventListener('pagehide', release);
    };
  }, [episodeId]);

  /* ---------------------------------------------------------------------
     Playback
     ------------------------------------------------------------------ */

  const parts = episode?.media.parts ?? [];
  const currentPart = parts[partIndex];

  const measured = episode ? Number.parseFloat(episode.measured_duration_seconds) : 0;

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.playbackRate = rate;
  }, [rate, partIndex]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play().catch((error: unknown) => {
      if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'NotAllowedError')) return;
      setLost('media');
    });
    else v.pause();
  }, []);

  const nudge = useCallback((seconds: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + seconds));
  }, []);

  const stepFrame = useCallback(
    (direction: number) => {
      const v = videoRef.current;
      if (!v) return;
      const fps = episode?.frame_rate ?? 30;
      v.pause();
      v.currentTime = Math.max(0, v.currentTime + direction / fps);
    },
    [episode?.frame_rate],
  );

  const changeRate = useCallback((direction: number) => {
    setRate((current) => {
      const index = RATES.findIndex((r) => Math.abs(r - current) < 0.01);
      const next = RATES[Math.min(RATES.length - 1, Math.max(0, (index < 0 ? 2 : index) + direction))];
      return next ?? current;
    });
  }, []);

  /* ---------------------------------------------------------------------
     Marking. Spans are what the client is allowed to send.
     ------------------------------------------------------------------ */

  const markIn = useCallback(() => {
    setSpans(current => markInSpan(current, position));
    setDecision('partial');
  }, [position]);

  const markOut = useCallback(() => {
    setSpans(current => markOutSpan(current, position));
  }, [position]);

  const clearAtPlayhead = useCallback(() => {
    setSpans(current => clearSpanAt(current, position));
  }, [position]);

  const closed = useMemo(() => spans.filter((s): s is { start: number; end: number } => s.end !== null), [spans]);
  const hasOpen = spans.some((s) => s.end === null);

  /**
   * The running figure, and the reason it is labelled an estimate.
   *
   * This sums the marked spans so a reviewer can see roughly what they are
   * about to award. It is not the payment: the server intersects, normalises,
   * converts exactly and rounds half away from zero in `quantise()`. Overlapping
   * marks in particular will total differently here and there — which is fine,
   * and is why the word "estimate" is on the screen and not in a comment.
   */
  const estimateSeconds = closed.reduce((total, s) => total + (s.end - s.start), 0);

  /* ---------------------------------------------------------------------
     The verdict
     ------------------------------------------------------------------ */

  const commit = useMutation({
    mutationFn: async () => {
      if (episode === null || decision === null || selected?.demo_override || claiming.current) return null;
      return api.verdict({
        verdict_id: verdictId,
        episode_id: episode.episode_id,
        decision,
        spans: decision === 'partial' ? closed.map((s) => ({ start_seconds: s.start, end_seconds: s.end })) : [],
        reject_reasons: decision === 'bad' ? reasons : [],
        reviewer_note: note.trim() === '' ? null : note.trim(),
        time_to_verdict_seconds: (Date.now() - claimedAt) / 1000,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shift'] });
      void queryClient.invalidateQueries({ queryKey: ['review', 'catalog'] });
      requestClaim();
    },
    /**
     * A failure here is one of three things and they are not interchangeable.
     * `commitFailure` reads which off the body; before it, every 409 became
     * `setLost('lease')` and a refusal the reviewer could act on was shown to
     * them as an expired claim.
     */
    onError: (err) => {
      const failure = commitFailure(err);
      if (failure.kind === 'lease') {
        setLost('lease');
        setLeaseError(err);
      } else if (failure.kind === 'refused') setRefused(failure);
    },
  });

  /**
   * Parks the episode the server refused, with the reviewer's own words.
   *
   * The episode leaves every claimable lane, so the loop this ends is real: the
   * review row stayed pending, the lease expired, and the queue handed the same
   * episode to the next reviewer to meet the same refusal.
   */
  const hold = useMutation({
    mutationFn: async () => {
      if (episode === null || refused === null) return null;
      /**
       * The refusal's own English sentence travels with the typed note. The
       * audit row and the counter screen are read by an operator who was not
       * here, and "no task claim" plus what the reviewer saw is the whole of
       * what they need; the reviewer's note alone would not say which refusal
       * it answers.
       */
      const written = holdReason.trim();
      const reason = `${MESSAGES.en[refused.key as keyof typeof MESSAGES.en] ?? refused.constraint ?? 'refused'}${
        written === '' ? '' : ` — ${written}`
      }`;
      return api.hold(episode.episode_id, reason.slice(0, 500));
    },
    onSuccess: () => {
      setHeld(true);
      void queryClient.invalidateQueries({ queryKey: ['shift'] });
    },
  });

  /**
   * Whether commit is allowed, mirroring the server's 422s.
   *
   * The server refuses a partial with no spans and a reject with no reasons,
   * and it is right to — but a reviewer should learn that from a disabled
   * button, not from a red box after they pressed it.
   */
  const canCommit =
    episode !== null &&
    decision !== null &&
    lost === null &&
    !held &&
    !claim.isPending &&
    !claim.isError &&
    !commit.isPending &&
    !demo.isPending &&
    !selected?.demo_override &&
    (decision !== 'partial' || closed.length > 0) &&
    (decision !== 'bad' || reasons.length > 0);

  /* ---------------------------------------------------------------------
     Keyboard. The whole screen is reachable without a pointer, because at
     40,000 hours every second per episode multiplies by tens of thousands.
     ------------------------------------------------------------------ */

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      /**
       * A tour dialog owns the keyboard while it is open, so Enter cannot
       * commit and I/O/X cannot mark. The signal is live and module-level, not
       * React state, because this listener runs in the same tick as the key it
       * is judging. The lease is untouched by any of it.
       */
      if (guideBlocksKeys(event)) return;
      if (episode === null) return;
      const target = event.target as HTMLElement | null;
      /** Never steal a key from a text field. */
      if (target?.closest('input, textarea, select, button, a')) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case ' ':
          event.preventDefault();
          togglePlay();
          return;
        case 'ArrowLeft':
          event.preventDefault();
          if (event.shiftKey) stepFrame(-1);
          else nudge(-5);
          return;
        case 'ArrowRight':
          event.preventDefault();
          if (event.shiftKey) stepFrame(1);
          else nudge(5);
          return;
        case 'j':
        case 'J':
          changeRate(-1);
          return;
        case 'l':
        case 'L':
          changeRate(1);
          return;
        case 'i':
        case 'I':
          markIn();
          return;
        case 'o':
        case 'O':
          markOut();
          return;
        case 'x':
        case 'X':
          clearAtPlayhead();
          return;
        case '1':
          setDecision('good');
          return;
        case '2':
          setDecision('partial');
          return;
        case '3':
          setDecision('bad');
          return;
        case 'Enter':
          if (canCommit && !claiming.current) commit.mutate();
          return;
        case '?':
          setShowShortcuts((s) => !s);
          return;
        case 'Escape':
          setShowShortcuts(false);
          return;
        default:
          return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [episode, togglePlay, nudge, stepFrame, changeRate, markIn, markOut, clearAtPlayhead, canCommit, commit]);

  /* ------------------------------------------------------------------ */

  const shellProps = {
    queueDepth: episode?.queue_depth,
    averageSeconds: episode?.session_average_seconds,
  };
  const queueSelector = (
    <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border)] bg-[var(--background)] px-4 py-3">
      <span className="text-sm font-semibold">{t('queue.select')}</span>
      {queues.map((lane, index) => <Button key={lane} variant={queue === lane ? 'primary' : 'outline'}
        aria-pressed={queue === lane} disabled={claim.isPending || commit.isPending || hold.isPending || demo.isPending}
        onClick={() => { commit.reset(); hold.reset(); demo.reset(); requestClaim(lane); }}>
        {t(lane === 'second_review' ? 'queue.secondReview' : `queue.${lane}`)} · {catalogs[index]?.data?.counts.total ?? '—'}
      </Button>)}
    </div>
  );
  const catalogView = <ReviewCatalog data={catalog.data ?? undefined} pending={catalog.isPending} error={catalog.error}
    selected={selected?.episode_id ?? null} busy={claim.isPending || commit.isPending || hold.isPending || demo.isPending}
    onSelect={item => { commit.reset(); hold.reset(); demo.reset(); requestClaim(item.queue, item); }}
    onRefresh={() => {
      const lane = queue;
      void catalog.refetch().then(({ data }) => {
        if (queueRef.current !== lane || claiming.current || !data) return;
        setSelected(current => current ? data.items.find(item => item.episode_id === current.episode_id) ?? current : null);
      });
    }} />;
  const demoControls = selected ? <DemoReviewControls key={`demo:${selected.episode_id}`} item={selected}
    busy={demo.isPending || claim.isPending || commit.isPending || hold.isPending} error={demo.error}
    onAction={(action, reason) => { if (claiming.current) return; claiming.current = true; demo.mutate({ item: selected, action, reason }); }} /> : null;

  /**
   * Playback is not authorised for this session, so there is no review to do.
   *
   * A whole screen rather than a banner over a live verdict panel: the server
   * refuses the claim as well as the footage, so nothing has been taken off the
   * queue and there is nothing here to decide. Offering the controls anyway
   * would invite a verdict on footage nobody watched, which is a payment.
   */
  if (claim.error instanceof ApiError && claim.error.isWithheld) {
    return (
      <AppShell {...shellProps} bleed>
        {queueSelector}
        {catalogView}
        <Nothing
          title={t('state.playbackWithheld.title')}
          body={t('state.playbackWithheld.body')}
          action={<WithheldQueue queue={queue} />}
        />
      </AppShell>
    );
  }

  if (claim.isError) {
    return (
      <AppShell {...shellProps} bleed>
        {queueSelector}
        {catalogView}
        <Nothing
          title={t('state.loadFailed.title')}
          body={claim.error.message}
          action={<Button variant="outline" onClick={() => requestClaim(queue, selected ?? undefined)}>{t('queue.refresh')}</Button>}
        />
      </AppShell>
    );
  }

  /** The queue is empty. A sentence on a drawn ground, and no mascot. */
  if (!claim.isPending && episode === null && !claim.isError) {
    return (
      <AppShell {...shellProps} bleed>
        {queueSelector}
        {catalogView}
        {selected?.claimable && !selected.demo_override ? <>{demoControls}<Nothing
          title={t('state.leaseExpired.title')}
          body={t('review.catalog.taken')}
          action={<Button variant="outline" onClick={() => requestClaim()}>{t('queue.refresh')}</Button>}
        /></> : selected ? <RecordingPreview key={`${selected.episode_id}:${selected.preview_url}`} item={selected} actions={demoControls} /> : null}
      </AppShell>
    );
  }

  return (
    <AppShell {...shellProps} bleed>
      {queueSelector}
      {catalogView}
      <div className="review-workbench">
        {/* ---------------- The theatre ---------------- */}
        <section className="on-stage flex min-w-0 flex-col">
          <div className="review-video-wrap flex min-h-0 flex-1 items-center justify-center p-4">
            {claim.isPending ? (
              <Skeleton className="aspect-video w-full max-w-[1100px] rounded-[var(--radius-lg)] bg-[var(--stage-panel)]" />
            ) : lost === 'media' ? (
              <div className="w-full max-w-[560px]">
                <Problem
                  onStage
                  title={t('state.mediaFailed.title')}
                  body={t('state.mediaFailed.body')}
                  action={
                    <div className="flex flex-wrap gap-2">
                      <Button variant="stage" onClick={() => setLost(null)}>{t('queue.refresh')}</Button>
                      <Button variant="stage" onClick={() => requestClaim()}>{t('state.mediaFailed.action')}</Button>
                    </div>
                  }
                />
              </div>
            ) : currentPart ? (
              <video
                ref={videoRef}
                key={`${currentPart.url}:${originalQuality}`}
                data-guide="review.player"
                src={originalQuality ? currentPart.url : optimizedUrl(currentPart.url)}
                className="max-h-full w-full max-w-[1100px] bg-[var(--stage)] ring-1 ring-[var(--stage-line)]"
                preload="auto"
                playsInline
                onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onError={() => setLost('media')}
                onEnded={() => {
                  if (partIndex < parts.length - 1) setPartIndex((i) => i + 1);
                }}
              />
            ) : null}
          </div>

          {/* Transport. Under the video, on the stage, never floating over it. */}
          <div className="border-t border-[var(--stage-line)] px-4 py-3 sm:px-6">
            <Scrubber
              position={position}
              measured={measured}
              spans={spans}
              onSeek={(seconds) => {
                const v = videoRef.current;
                if (v) v.currentTime = seconds;
              }}
            />

            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="text-[0.75rem] text-[var(--stage-mid)]">{t('review.catalog.quality')}
                <select value={originalQuality ? 'original' : 'preview'} onChange={event => { setOriginalQuality(event.currentTarget.value === 'original'); setLost(null); }}
                  className="ml-2 min-h-11 rounded border border-[var(--stage-line)] bg-[var(--stage)] px-2 text-[var(--stage-fg)]">
                  <option value="preview">{t('review.catalog.optimized')}</option><option value="original">{t('review.catalog.original')}</option>
                </select>
              </label>
              <Button variant="stage" size="sm" onClick={togglePlay}>
                {playing ? t('player.pause') : t('player.play')}
                <Key onStage>Space</Key>
              </Button>

              <span className="num text-[0.875rem] font-medium text-[var(--stage-fg)]">
                {duration(position)}
                <span className="text-[var(--stage-mid)]"> / {duration(measured)}</span>
              </span>

              <span className="num text-[0.75rem] text-[var(--stage-mid)]">{rate.toFixed(2)}×</span>

              {parts.length > 1 ? (
                <span className="text-[0.8125rem] text-[var(--stage-mid)]">
                  {t('player.part')} {partIndex + 1} {t('player.of')} {parts.length}
                </span>
              ) : null}

              <div data-guide="review.marks" className="ml-auto flex items-center gap-2">
                <Button variant="stage" size="sm" onClick={markIn}>
                  {t('mark.in')}
                  <Key onStage>I</Key>
                </Button>
                <Button variant="stage" size="sm" onClick={markOut} disabled={!hasOpen}>
                  {t('mark.out')}
                  <Key onStage>O</Key>
                </Button>
                <Button variant="stage" size="sm" onClick={clearAtPlayhead}>
                  {t('mark.clear')}
                  <Key onStage>X</Key>
                </Button>
                <Button
                  variant="stage"
                  size="icon"
                  onClick={() => setShowShortcuts((s) => !s)}
                  title={t('shortcuts.title')}
                >
                  <IconKeyboard size={17} />
                </Button>
              </div>
            </div>

            {/*
              The running total: this screen's one feature figure, and the only
              place mono is allowed to be large here. It sits on the theatre's
              own ink rather than in a block of its own — the stage IS the ink
              block, and a second dark panel inside it would be two darks that
              nearly match. It is `--stage-fg` and not sun: sun is action, and a
              figure is not an action.
            */}
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-[var(--stage-line)] pt-3">
              <span className="num text-[2.0625rem] font-medium leading-none tracking-[-0.03em] text-[var(--stage-fg)]">
                {duration(estimateSeconds)}
              </span>
              <span className="text-[0.8125rem] font-semibold text-[var(--stage-fg)]">
                {t('mark.estimate')}
              </span>
              <span className="text-[0.8125rem] text-[var(--stage-mid)]">
                {t('mark.estimateHint')}
              </span>
            </div>
          </div>
        </section>

        {/* ---------------- The rail: what the machine knows, then the decision ---------------- */}
        <aside className="flex flex-col border-l border-[var(--border)] bg-[var(--background)]">
          {demoControls}
          {lost === 'lease' ? (
            <div className="p-4">
              <Problem
                reference={leaseError instanceof ApiError ? leaseError.ref : undefined}
                title={t('state.leaseExpired.title')}
                body={t('state.leaseExpired.body')}
                action={
                  <Button variant="primary" onClick={() => requestClaim()}>
                    {t('state.leaseExpired.action')}
                  </Button>
                }
              />
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto p-4">
            {claim.isPending || episode === null ? (
              <div className="flex flex-col gap-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-28 w-full" />
              </div>
            ) : (
              <>
                <h1 className="num break-all border-b border-[var(--foreground)] pb-2 text-[0.9375rem] font-bold leading-snug">
                  {episode.session_folder}
                </h1>

                <dl className="mt-3 divide-y divide-[var(--border)]">
                  <Field
                    label={t('meta.task')}
                    value={episode.task?.name ?? t('meta.unknown')}
                  />
                  <Field
                    label={t('meta.rate')}
                    value={
                      episode.task
                        ? `${money(episode.task.price_per_minute, episode.task.currency)} / min`
                        : t('meta.unknown')
                    }
                  />
                  <Field
                    label={t('meta.collector')}
                    value={episode.collector?.display_name ?? t('meta.unknown')}
                    tone="data"
                  />
                  <Field label={t('meta.measured')} value={duration(episode.measured_duration_seconds)} />
                  <Field
                    label={t('meta.claimed')}
                    value={duration(episode.claimed_duration_seconds)}
                  />
                  {episode.claimed_duration_seconds ? (
                    <Field
                      label={t('meta.discrepancy')}
                      tone="warn"
                      value={`${signedSeconds(episode.claimed_duration_seconds, episode.measured_duration_seconds)} · ${signedPercent(episode.claimed_duration_seconds, episode.measured_duration_seconds)}`}
                    />
                  ) : null}
                  <Field
                    label={t('meta.device')}
                    value={episode.device.serial ?? t('meta.unknown')}
                  />
                </dl>

                {/* The declaration a collector made before recording. APP-17b. */}
                {episode.declared ? (
                  <div className="mt-5">
                    <p className="border-b border-[var(--border-strong)] pb-1 text-[0.8125rem] font-semibold text-[var(--foreground)]">
                      {t('meta.declared')}
                    </p>
                    <dl className="mt-1 divide-y divide-[var(--border)]">
                      <Field
                        label={t('meta.othersInFrame')}
                        value={episode.declared.others_in_frame ? t('meta.yes') : t('meta.no')}
                      />
                      <Field
                        label={t('meta.sensitive')}
                        value={episode.declared.sensitive_info_present ? t('meta.yes') : t('meta.no')}
                      />
                    </dl>
                  </div>
                ) : null}

                {episode.flags.length > 0 ? (
                  <details className="mt-5">
                    <summary>{t('meta.flags')} · {episode.flags.length}</summary>
                    <div className="divide-y divide-[var(--border)]">
                      {episode.flags.map((f) => (
                        <FlagRow
                          key={f.code}
                          code={f.code}
                          description={t(`bo.flag.${f.code}`)}
                          detail={f.detail}
                          blocking={f.blocks_review}
                        />
                      ))}
                    </div>
                  </details>
                ) : null}
              </>
            )}
          </div>

          {/* The decision. Pinned, because a verdict never requires scrolling. */}
          <div className="border-t border-[var(--border)] bg-[var(--card)] p-4">
            <div
              data-guide="review.verdict"
              className="grid grid-cols-3 gap-2"
              role="radiogroup"
              aria-label={t('app.review')}
            >
              <VerdictChoice
                value="good"
                current={decision}
                onSelect={setDecision}
                label={t('verdict.good')}
                shortcut="1"
                Glyph={IconPass}
                fg="var(--pass)"
                bg="var(--pass-bg)"
              />
              <VerdictChoice
                value="partial"
                current={decision}
                onSelect={setDecision}
                label={t('verdict.partial')}
                shortcut="2"
                Glyph={IconPartial}
                fg="var(--partial)"
                bg="var(--partial-bg)"
              />
              <VerdictChoice
                value="bad"
                current={decision}
                onSelect={setDecision}
                label={t('verdict.bad')}
                shortcut="3"
                Glyph={IconReject}
                fg="var(--reject)"
                bg="var(--reject-bg)"
              />
            </div>

            {/* QR-04: a collector paid nothing has to be told why, in their language. */}
            {decision === 'bad' ? (
              <fieldset
                data-guide="review.reasons"
                className="mt-3 max-h-40 overflow-y-auto border-t border-[var(--border-strong)] pt-2"
              >
                <legend className="sr-only">{t('verdict.reasons')}</legend>
                <p className="px-1 pb-1 text-[0.8125rem] font-semibold">{t('verdict.reasons')}</p>
                {(reasonQuery.data?.reasons ?? []).map((r: ReasonCode) => (
                  <label
                    key={r.code}
                    className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-[0.8125rem] hover:bg-[var(--muted)]"
                  >
                    <input
                      type="checkbox"
                      checked={reasons.includes(r.code)}
                      onChange={(e) =>
                        setReasons((current) =>
                          e.currentTarget.checked
                            ? [...current, r.code]
                            : current.filter((c) => c !== r.code),
                        )
                      }
                      className="mt-0.5 accent-[var(--action)]"
                    />
                    <span>{i18n.language === 'zh' ? r.label_zh : r.label_en}</span>
                  </label>
                ))}
                {reasons.length === 0 ? (
                  <p className="px-1 pt-1 text-[0.75rem] text-[var(--reject)]">
                    {t('verdict.reasonsRequired')}
                  </p>
                ) : null}
              </fieldset>
            ) : null}

            {decision === 'partial' && closed.length === 0 ? (
              <p className="mt-2 text-[0.8125rem] text-[var(--partial)]">{t('mark.needsSpan')}</p>
            ) : null}

            <textarea
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              placeholder={t('verdict.note')}
              rows={2}
              className="mt-3 w-full resize-none rounded-[var(--radius-base)] border border-[var(--border-strong)] bg-[var(--card)] px-3 py-2 text-[0.875rem] focus:border-[var(--action)] focus:outline-none"
            />

            {/*
              The refusal, in the reader's own language, and the way out of it.

              This block is the fix for two defects at once. The old code hid
              the error box for every 409 and showed the lease banner instead,
              so a reviewer met "the claim expired, take the next one" for
              refusals that had nothing to do with a lease — and there was no
              action on the screen for the ones the sentence tells them to send
              back to the counter.
            */}
            {refused !== null ? (
              <div data-guide="review.hold" className="mt-3">
                {held ? (
                  <Problem title={t('state.refused.held.title')} body={t('state.refused.held.body')} />
                ) : (
                  <Problem
                    reference={commit.error instanceof ApiError ? commit.error.ref : undefined}
                    title={t('state.refused.title')}
                    body={t(refused.key)}
                    action={
                      refused.holdable ? (
                        <div className="flex flex-col gap-2">
                          <label className="text-[0.75rem] font-semibold text-[var(--muted-foreground)]">
                            {t('state.refused.holdReason')}
                            <textarea
                              value={holdReason}
                              onChange={(e) => setHoldReason(e.currentTarget.value)}
                              rows={2}
                              className="mt-1 w-full resize-none rounded-[var(--radius-base)] border border-[var(--border-strong)] bg-[var(--card)] px-3 py-2 text-[0.875rem] font-normal text-[var(--foreground)] focus:border-[var(--action)] focus:outline-none"
                            />
                          </label>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={hold.isPending}
                            onClick={() => hold.mutate()}
                          >
                            {hold.isPending ? t('state.refused.holding') : t('state.refused.hold')}
                          </Button>
                          {hold.isError ? (
                            <p role="alert" className="text-[0.75rem] font-medium text-[var(--reject)]">
                              {t('state.refused.holdFailed')}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <Button variant="primary" size="sm" onClick={() => requestClaim()}>
                          {t('state.leaseExpired.action')}
                        </Button>
                      )
                    }
                  />
                )}
                {held ? (
                  <Button variant="primary" size="sm" className="mt-3" onClick={() => requestClaim()}>
                    {t('state.leaseExpired.action')}
                  </Button>
                ) : null}
              </div>
            ) : commit.isError && !(commit.error instanceof ApiError && commit.error.isReassigned) ? (
              <p role="alert" className="mt-2 text-[0.8125rem] font-medium text-[var(--reject)]">
                {t('state.writeFailed.title')} — {(commit.error as Error).message}
              </p>
            ) : null}

            <Button
              variant="primary"
              size="lg"
              data-guide="review.next"
              className="mt-3 w-full"
              disabled={!canCommit}
              onClick={() => commit.mutate()}
            >
              {commit.isPending ? t('verdict.committing') : t('verdict.commit')}
              <Key>↵</Key>
            </Button>
          </div>
        </aside>
      </div>

      {showShortcuts ? <Shortcuts onClose={() => setShowShortcuts(false)} /> : null}
    </AppShell>
  );
}

function VerdictChoice({
  value,
  current,
  onSelect,
  label,
  shortcut,
  Glyph,
  fg,
  bg,
}: {
  value: Verdict;
  current: Verdict | null;
  onSelect: (v: Verdict) => void;
  label: string;
  shortcut: string;
  Glyph: typeof IconPass;
  fg: string;
  bg: string;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={() => onSelect(value)}
      className={cn(
        'flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-2',
        'text-[0.8125rem] font-semibold transition-colors duration-150 ease-[var(--ease)]',
        active
          ? 'font-bold'
          : 'border-[var(--border-strong)] text-[var(--muted-foreground)] hover:border-[var(--foreground)] hover:text-[var(--foreground)]',
      )}
      style={active ? { borderColor: fg, backgroundColor: bg, color: fg } : undefined}
    >
      {/*
        Nothing here shrinks. The first draft let the label truncate inside the
        pill and the reject button rendered as "Rej…" on a 1440px screen — an
        abbreviated verdict on the control that decides whether somebody is
        paid. The row is sized so the longest label in all three locales fits;
        if a future one does not, the pill grows and the rail gives up the
        width, because the word is not the part that may go.
      */}
      <Glyph size={16} className="shrink-0" />
      <span className="shrink-0">{label}</span>
      <Key>{shortcut}</Key>
    </button>
  );
}

/**
 * Nothing to do, on this screen only.
 *
 * The shared empty state carries Trúc, and nothing cartoon goes on the screen
 * where footage is judged — not even when the queue reaches zero. So this is
 * the same drawn hatch and the same measure, with a sentence where the mascot
 * would be. It lives here rather than in `components/ui` because it is the one
 * screen with that rule.
 */
/**
 * What a reviewer CAN see when raw playback is withheld: the queue's metadata.
 *
 * `GET /api/review/next` is not behind `mayWatch` — only the claim, the
 * heartbeat and the verdict are (review.ts) — and it answers 200 with the
 * task, the collector, the device and both durations, with `media.parts`
 * empty. Measured 2026-09-15 and again on 2026-09-16 against the stakeholder
 * seed. So the screen shows that instead of implying the deployment is broken,
 * and it does not offer a verdict control: the server refuses the verdict for
 * this session by name, and a button that cannot honestly be pressed is the
 * thing this screen already exists to avoid.
 *
 * ponytail: a read, not a queue. One episode, because that is what the route
 * answers; a reviewer's whole list is a route nobody has needed yet.
 */
function WithheldQueue({ queue }: { queue: ReviewQueue }) {
  const { t } = useTranslation();
  const next = useQuery({ queryKey: ['review', 'peek', queue], queryFn: () => api.peek(queue), retry: false });
  if (next.isPending || next.error) return null;
  const episode = next.data;
  if (!episode) return <p className="text-[0.9375rem]">{t('queue.empty.body')}</p>;
  return (
    <div>
      <p className="text-[0.9375rem] font-semibold">{t('state.playbackWithheld.metadata')}</p>
      <dl className="mt-3 space-y-2">
        <Field label={t('meta.task')} value={episode.task?.name ?? t('meta.unknown')} />
        <Field label={t('meta.collector')} value={episode.collector?.display_name ?? t('meta.unknown')} />
        <Field label={t('meta.device')} value={episode.device.serial ?? t('meta.unknown')} />
        <Field label={t('meta.measured')} value={duration(episode.measured_duration_seconds)} />
      </dl>
    </div>
  );
}

function Nothing({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-[240px] items-center justify-center p-6">
      <div className="hatch w-full max-w-[46ch] rounded-[var(--radius-lg)] border border-[var(--border)] px-8 py-12">
        <h2 className="text-[1.625rem] font-extrabold leading-[1.15] tracking-[-0.03em]">{title}</h2>
        <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--muted-foreground)]">{body}</p>
        {action ? <div className="mt-6">{action}</div> : null}
      </div>
    </div>
  );
}

/**
 * The shortcut sheet.
 *
 * A `<dialog>` rather than a positioned div, so it escapes every `overflow`
 * ancestor, traps focus, and closes on Escape without any of that being
 * written here.
 */
function Shortcuts({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const rows: [string, string][] = [
    ['Space', t('shortcuts.playPause')],
    ['← →', t('shortcuts.seek')],
    ['⇧ ← →', t('shortcuts.frame')],
    ['J L', t('shortcuts.rate')],
    ['I', t('shortcuts.markIn')],
    ['O', t('shortcuts.markOut')],
    ['X', t('shortcuts.clear')],
    ['1 2 3', t('shortcuts.verdict')],
    ['↵', t('shortcuts.commit')],
    ['?', t('shortcuts.help')],
  ];

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="m-auto w-[min(30rem,92vw)] rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--card)] p-0 text-[var(--foreground)] backdrop:bg-[var(--scrim)]"
    >
      <div className="p-5">
        <h2 className="text-[1.0625rem] font-bold">{t('shortcuts.title')}</h2>
        <dl className="mt-4 divide-y divide-[var(--border)]">
          {rows.map(([keys, description]) => (
            <div key={keys} className="flex items-center justify-between gap-4 py-2">
              <dt className="num text-[0.8125rem] font-semibold">{keys}</dt>
              <dd className="text-[0.8125rem] text-[var(--muted-foreground)]">{description}</dd>
            </div>
          ))}
        </dl>
        <Button variant="outline" className="mt-4 w-full" onClick={() => ref.current?.close()}>
          {t('shortcuts.show')}
        </Button>
      </div>
    </dialog>
  );
}
